import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, Chip } from '@mui/material';
import * as yup from 'yup';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import ConfirmDialog from 'components/confirm-dialog/ConfirmDialog';
import InsideLoading from 'components/loading/InsideLoading';
import InsideSaving from 'components/loading/InsideSaving';
import NoData from 'components/no-data/NoData';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import { normalizeDate } from 'helpers/parsePtBRDate';
import { api } from 'services/api';
import history from 'services/history';
import { useSelector } from 'store/selector';
import { UserProfile } from 'types/userProfile';
import UserForm, { UserForm as UserFormInterface } from '../form/UserForm';
import { useUserValidation } from '../validation/useUserValidation';

const EditUser: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const loggedUser = useSelector(state => state.user);
  const [validation, setValidation, validate] = useUserValidation();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [user, setUser] = useState<UserFormInterface | null>(null);
  const [saved, setSaved] = useState<UserFormInterface | null>(null);
  const [userProfiles, setUserProfiles] = useState<UserProfile[]>([]);

  const isSelf = !!user && loggedUser?.id === user.id;

  const loadUser = useCallback(() => {
    if (!id) return Promise.resolve();

    return Promise.all([api.get(`/users/${id}`), api.get('/user-profiles')])
      .then(([userResponse, profilesResponse]) => {
        const userData = userResponse.data;
        const loaded: UserFormInterface = {
          id: userData.id,
          username: userData.username,
          email: userData.email,
          name: userData.name,
          phone: userData.phone,
          rg: userData.rg,
          document_number: userData.document_number,
          role: userData.role,
          active: userData.active,
          address: userData.address,
          birth_date: userData.birth_date ? normalizeDate(userData.birth_date) : null,
          created_at: userData.created_at,
          email_verified_at: userData.email_verified_at,
          updated_at: userData.updated_at,
          password: '',
          password_confirmation: '',
          user_profile: userData.user_profile,
          user_profile_id: userData.user_profile_id,
        };
        setUser(loaded);
        setSaved(loaded);
        setUserProfiles(profilesResponse.data);
      })
      .catch(() => {
        toast.error('Não foi possível carregar os dados do usuário');
        history.push('/users');
      });
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadUser().finally(() => setLoading(false));
  }, [loadUser]);

  function goBack() {
    history.push('/users');
  }

  function handleChange<K extends keyof UserFormInterface>(index: K, value: UserFormInterface[K]) {
    setUser(prev => (prev ? { ...prev, [index]: value } : null));
  }

  function handleSubmit() {
    if (!user) return;

    setSaving(true);

    const data = {
      id: user.id,
      name: user.name,
      phone: user.phone,
      rg: user.rg,
      document_number: user.document_number,
      role: user.role,
      active: user.active,
      address: user.address,
      password: user.password ? user.password : undefined,
      password_confirmation: user.password_confirmation ? user.password_confirmation : undefined,
      user_profile_id: user.user_profile_id,
      birth_date: user.birth_date ? format(user.birth_date, 'yyyy-MM-dd HH:mm:ss') : '',
    };

    api
      .put(`/users/${user.id}`, data)
      .then(() => loadUser())
      .then(() => toast.success('Usuário atualizado'))
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível atualizar o usuário');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (!user) return;

    validate(user, 'edit')
      .then(() => {
        handleSubmit();
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  // descarta o que foi digitado e recarrega o cadastro
  function handleCancel() {
    setValidation({});
    setLoading(true);
    loadUser().finally(() => setLoading(false));
  }

  // "excluir" só inativa: o histórico (guias, orçamentos) continua apontando para o usuário
  function handleInactivateUser() {
    if (!user) return;

    setSaving(true);

    api
      .delete(`/users/${user.id}`)
      .then(() => {
        toast.success('Usuário inativado com sucesso!');
        history.push('/users');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível inativar o usuário');
      })
      .finally(() => {
        setSaving(false);
        setConfirmDialogOpen(false);
      });
  }

  return (
    <>
      <Appbar title="Usuários" backAction={goBack} />

      {saving && <InsideSaving />}

      {confirmDialogOpen && (
        <ConfirmDialog
          confirmLabel="Inativar"
          destructive
          handleNoClick={() => setConfirmDialogOpen(false)}
          handleYesClick={handleInactivateUser}
          title="Deseja inativar este usuário? Ele não conseguirá mais entrar no sistema."
        />
      )}

      <PageShell>
        <RecordHeader title={saved?.name || 'Usuário'} active={!!saved?.active} backAction={goBack}>
          {saved?.username && <Chip size="small" variant="outlined" label={`@${saved.username}`} />}
          {saved?.user_profile?.name && (
            <Chip size="small" variant="outlined" label={`Perfil: ${saved.user_profile.name}`} />
          )}
        </RecordHeader>

        <WorkArea>
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !user ? (
              <NoData message="Não foi possível carregar os dados do usuário" />
            ) : (
              <StyledForm onSubmit={handleValidation}>
                <UserForm
                  type="edit"
                  handleChange={handleChange}
                  user={user}
                  validation={validation}
                  userProfiles={userProfiles}
                  isSelf={isSelf}
                />
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        {!loading && user && (
          <RecordFooter sx={{ justifyContent: 'space-between' }}>
            <Button color="error" size="small" disabled={saving || isSelf} onClick={() => setConfirmDialogOpen(true)}>
              Inativar usuário
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outlined" color="inherit" size="small" disabled={saving} onClick={handleCancel}>
                Cancelar
              </Button>
              <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
                Salvar alterações
              </Button>
            </div>
          </RecordFooter>
        )}
      </PageShell>
    </>
  );
};

export default EditUser;
